import FullScreenDialog from "../../../../common/core-ui/FullScreenDialog.tsx";
import { FormattedMessage, useIntl } from "react-intl";
import {
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./QrCodeDialog.module.scss";
import { clsx } from "clsx";
import {
  AppleAppStore,
  ChromeBookIcon,
  DevicesGraphic,
  GooglePlayStore,
  IosIcon,
  WindowsIcon,
} from "./graphics.tsx";
import QrCode from "../../../../common/core-ui/QrCode.tsx";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import UserAvatar from "../../common/UserAvatar.tsx";
import avatarBackground from "./avatar-background.png";
import { useBrandLoaderData } from "../../../root/rootLoader.ts";
import { useDynamicLinkFetcher } from "../user/user.ts";
import useReadOffline from "../../layout/sidebar/my-account/read-offline/useReadOffline.ts";

type QRCodeDialogProps = Omit<
  ComponentPropsWithoutRef<typeof FullScreenDialog>,
  "children"
> & {
  onOpenAppDialog: (url: string) => void;
};

function MobileAppLink(
  props: ComponentPropsWithoutRef<typeof BaseLinkOrButton>
) {
  return <BaseLinkOrButton {...props} className={styles.appLink} />;
}

function DesktopAppLink(
  props: ComponentPropsWithoutRef<typeof BaseLinkOrButton>
) {
  return <BaseLinkOrButton {...props} className="vst-link"></BaseLinkOrButton>;
}

const AUTO_REFRESH_LIMIT = 3;
const EXPIRES = 600000; // 10 minutes

function QRCodeDialogInner({
  onOpenAppDialog,
}: {
  onOpenAppDialog: (url: string) => void;
}) {
  const intl = useIntl();
  const brand = useBrandLoaderData();
  const linkFetcher = useDynamicLinkFetcher();
  const [requiresManualRefresh, setRequiresManualRefresh] = useState(false);
  const refreshCount = useRef(0);
  const { launchApp } = useReadOffline();
  const macUrl = brand.DownloadOffline.mac_url;
  const winUrl = brand.DownloadOffline.wins_url;
  const chromeUrl = brand.DownloadOffline.chrome_url;

  const downloadApp = useCallback(
    (url: string) => {
      launchApp();
      onOpenAppDialog(url);
    },
    [launchApp, onOpenAppDialog]
  );

  const fetchLink = useCallback(() => {
    const params = new URLSearchParams();
    params.set("destination", `${brand.DownloadOffline.protocol}bookshelf`);
    linkFetcher.load(`/actions/user/dynamic-link?${params.toString()}`);
  }, [brand, linkFetcher]);

  useEffect(() => {
    if (linkFetcher.state === "idle" && !linkFetcher.data) {
      fetchLink();
    }
  }, [fetchLink, linkFetcher]);

  useEffect(() => {
    setRequiresManualRefresh(false);
  }, [linkFetcher.data]);

  useEffect(() => {
    if (requiresManualRefresh || !linkFetcher.data) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      if (refreshCount.current < AUTO_REFRESH_LIMIT) {
        refreshCount.current++;
        fetchLink();
      } else {
        setRequiresManualRefresh(true);
      }
    }, EXPIRES);

    return () => clearTimeout(timeoutId);
  }, [fetchLink, linkFetcher.data, requiresManualRefresh]);

  const mobileAppLinks = useMemo(
    () =>
      [
        {
          label: intl.formatMessage({
            defaultMessage: "Apple app store",
          }),
          icon: <AppleAppStore />,
          href: brand.DownloadOffline.ios_url,
        },
        {
          label: intl.formatMessage({
            defaultMessage: "Google Play app store",
          }),
          icon: <GooglePlayStore />,
          href: brand.DownloadOffline.android_url,
        },
      ].filter((el) => el.href),
    [brand, intl]
  );

  const desktopAppLinks = useMemo(
    () =>
      [
        {
          label: intl.formatMessage({
            defaultMessage: "Mac",
          }),
          icon: <IosIcon />,
          onClick: () => downloadApp(macUrl),
          enabled: !!macUrl,
        },
        {
          label: intl.formatMessage({
            defaultMessage: "Windows",
          }),
          icon: <WindowsIcon />,
          onClick: () => downloadApp(winUrl),
          enabled: !!winUrl,
        },
        {
          label: intl.formatMessage({
            defaultMessage: "Chromebook",
          }),
          icon: <ChromeBookIcon />,
          onClick: () => downloadApp(chromeUrl),
          enabled: !!chromeUrl,
        },
      ].filter((el) => el.enabled),
    [macUrl, winUrl, chromeUrl, intl, downloadApp]
  );

  return (
    <>
      <h2 className={styles.header}>
        <FormattedMessage defaultMessage="Start Reading or Listening On the Go!" />
      </h2>
      <div
        className={clsx(
          "vst-card",
          "vst-card-variant-raised",
          styles.mainContentBox
        )}
      >
        <DevicesGraphic className={styles.headerGraphic} />
        <div className={styles.contentBody}>
          <div>
            <h3>
              <FormattedMessage defaultMessage="Login Instantly!" />
            </h3>
            <p>
              <FormattedMessage defaultMessage="Use your device's camera to scan the QR code on the right to login to the mobile app instantly." />
            </p>
            <h4>
              <FormattedMessage
                defaultMessage="Don't have the {appName} App?"
                values={{ appName: brand.DownloadOffline.appName }}
              />
            </h4>
            <p>
              <FormattedMessage defaultMessage="Scan the QR code to download the Bookshelf mobile app from the App Store or Google Play." />
            </p>
            <div className={styles.appLinks}>
              {mobileAppLinks.map(({ label, icon, href }) => (
                <MobileAppLink key={label} aria-label={label} href={href}>
                  {icon}
                </MobileAppLink>
              ))}
            </div>
          </div>
          <QrCode
            isExpired={requiresManualRefresh}
            onRequestRefresh={() => fetchLink()}
            value={linkFetcher.data?.data?.shortLink}
            label={intl.formatMessage({
              defaultMessage: "QR code to download app",
            })}
            imageOverlay={<UserAvatar />}
            imageSettings={{
              src: avatarBackground,
              height: 80,
              width: 80,
              excavate: false,
            }}
          />
        </div>
        <div className={styles.footnote}>
          <FormattedMessage
            defaultMessage="*Available on app version {version} and higher"
            values={{ version: "10.1" }}
          />
        </div>
      </div>
      <div className={styles.desktopPromoArea}>
        {desktopAppLinks.length > 0 && (
          <>
            <FormattedMessage defaultMessage="You can also download apps for desktop!" />
            <div className={styles.desktopLinks}>
              {desktopAppLinks.map(
                ({ label, icon, onClick, enabled }) =>
                  enabled && (
                    <DesktopAppLink onClick={onClick} key={label}>
                      {icon}
                      {label}
                    </DesktopAppLink>
                  )
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default function QrCodeDialog({
  onRequestClose,
  onOpenAppDialog,
}: QRCodeDialogProps) {
  return (
    <FullScreenDialog
      onRequestClose={onRequestClose}
      closeStyle="white"
      className={styles.background}
    >
      <QRCodeDialogInner onOpenAppDialog={onOpenAppDialog} />
    </FullScreenDialog>
  );
}
